import React, { useContext, useEffect, useMemo, useState } from "react";
import { Redirect, useLocation } from "react-router-dom";
import ReturnBar from "../Navbar/ReturnBar";
import { UserContext, RoleType } from "app/contexts/UserContext";
import { SelectedCoachContext } from "app/contexts/SelectedCoachContext";
import { Loading } from "app/components/Wrappers";

const SELECTED_COACH_STORAGE_KEY = "calendarSettings_selectedCoachId";

interface ICalendarSettingsLayout {
  children: React.ReactNode;
}

const CalendarSettingsLayout = ({ children }: ICalendarSettingsLayout) => {
  const user = useContext(UserContext);
  const facility = user.facility;
  const location = useLocation<{ fromCalendarSettings?: boolean }>();

  // Don't show coach selector on Calendar Integrations page - only coaches can edit their own integrations
  const isIntegrationsPage = location.pathname === "/calendar/settings";

  const coaches = useMemo(() => {
    return facility?.coachProfiles || [];
  }, [facility]);

  const isAdminOrScheduler = user.behavesLikeAdmin || user.role === RoleType.SCHEDULER;

  const canSelectCoach = useMemo(() => {
    return (
      isAdminOrScheduler &&
      facility?.featureFlags?.adminsCanEditCoachSettings === true &&
      coaches.length > 0
    );
  }, [isAdminOrScheduler, facility?.featureFlags?.adminsCanEditCoachSettings, coaches]);

  // Initialize from sessionStorage (set by gear icon click) or fall back to user ID
  const getInitialCoachId = () => {
    const stored = sessionStorage.getItem(SELECTED_COACH_STORAGE_KEY);
    if (stored && coaches.some((c) => c.id === stored)) {
      return stored;
    }
    return user.id || null;
  };

  const [selectedCoachId, setSelectedCoachIdState] = useState<string | null>(getInitialCoachId);

  // Custom setter that also saves to sessionStorage
  const setSelectedCoachId = (id: string) => {
    setSelectedCoachIdState(id);
    sessionStorage.setItem(SELECTED_COACH_STORAGE_KEY, id);
  };

  // Restore from sessionStorage when coaches data becomes available
  useEffect(() => {
    if (canSelectCoach && coaches.length > 0) {
      const stored = sessionStorage.getItem(SELECTED_COACH_STORAGE_KEY);
      if (stored && coaches.some((c) => c.id === stored)) {
        // Coach&Admin users should default to themselves unless they explicitly
        // navigated here via the gear icon (fromCalendarSettings).
        // Without this guard, a stale sessionStorage value from a previous
        // gear-icon navigation overwrites the correct self-default.
        const isCoachAdmin = user.kind === "coach";
        const fromGearIcon = location.state?.fromCalendarSettings;
        if (isCoachAdmin && stored !== user.id && !fromGearIcon) {
          setSelectedCoachIdState(user.id);
        } else {
          setSelectedCoachIdState(stored);
        }
      }
    }
  }, [canSelectCoach, coaches]);

  // Sync state when user ID becomes available (for non-admin users)
  useEffect(() => {
    if (user.id && !selectedCoachId && !canSelectCoach) {
      setSelectedCoachIdState(user.id);
    }
  }, [user.id, selectedCoachId, canSelectCoach]);

  const selectedCoach = useMemo(() => {
    return coaches.find((c) => c.id === selectedCoachId) || null;
  }, [coaches, selectedCoachId]);

  // On integrations page, always use current user's ID (admins can't edit other coaches' integrations)
  const effectiveCanSelectCoach = canSelectCoach && !isIntegrationsPage;

  // For admins/schedulers who might be navigating from the gear icon,
  // check if there's a stored coach ID in sessionStorage that we should use
  // even before canSelectCoach becomes true (while waiting for facility data to load)
  const getEffectiveCoachId = () => {
    if (effectiveCanSelectCoach) {
      // For admins with coach selection, use selected or default to first coach
      if (selectedCoachId && coaches.some((c) => c.id === selectedCoachId)) {
        return selectedCoachId;
      }
      // For Coach&Admin users, default to themselves if they're in the coaches list
      if (user.kind === "coach" && user.id && coaches.some((c) => c.id === user.id)) {
        return user.id;
      }
      // Fall back to first coach for pure admins/schedulers
      return coaches[0]?.id || null;
    }

    // Coaches (even those with facility_admin attribute that makes behavesLikeAdmin true)
    // should always use their own ID unless effectiveCanSelectCoach is true
    if (user.kind === "coach") {
      return user.id || null;
    }

    // If user is a FacilityAdmin/Scheduler but canSelectCoach is false (facility not loaded yet),
    // try to use the sessionStorage value to avoid using user.id which might not be a coach ID
    if (isAdminOrScheduler) {
      const stored = sessionStorage.getItem(SELECTED_COACH_STORAGE_KEY);
      if (stored) {
        return stored;
      }
      // Default to first coach if available
      if (coaches.length > 0) {
        return coaches[0].id;
      }
    }

    // Fall back to user.id for other user types
    return user.id || null;
  };

  const contextValue = useMemo(
    () => ({
      selectedCoachId: getEffectiveCoachId(),
      setSelectedCoachId,
      selectedCoach,
      coaches,
      canSelectCoach: effectiveCanSelectCoach,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedCoachId, selectedCoach, coaches, effectiveCanSelectCoach, user.id, isAdminOrScheduler]
  );

  // Centralized access control - prevents flicker by handling all checks in one place
  const isCoach = user.kind === "coach";

  // Admins/schedulers need facility data loaded before we can check access
  if (isAdminOrScheduler && !facility) {
    return <Loading />;
  }

  // Check access once facility is loaded (or immediately for coaches)
  const adminCanAccess = isAdminOrScheduler && facility?.featureFlags?.adminsCanEditCoachSettings;
  if (!isCoach && !adminCanAccess) {
    return <Redirect to="/calendar/facility" />;
  }

  // Redirect from coach pricing page when facility-level booking options are enabled
  const isPricingPage = location.pathname === "/calendar/pricing";
  const facilityBookingEnabled = facility?.featureFlags?.facilityLevelBookingOptions;
  if (isPricingPage && facilityBookingEnabled) {
    return <Redirect to="/calendar/availability" />;
  }

  return (
    <SelectedCoachContext.Provider value={contextValue}>
      <div className="calendar-settings-container">
        <ReturnBar />
        {children}
      </div>
    </SelectedCoachContext.Provider>
  );
};

export default CalendarSettingsLayout;
